/**
 * Converts a value into the exact line and token model that
 * `JSON.stringify(value, null, 2)` produces, so a rendered view and the
 * clipboard can never diverge. Nothing here sorts, renames, or reformats.
 */

export type TokenKind =
  | 'key'
  | 'string'
  | 'number'
  | 'boolean'
  | 'null'
  | 'punctuation'
  | 'muted'

export type JsonToken = {
  text: string
  kind: TokenKind
}

export type JsonFold = {
  /** Stable identifier for the container this line opens. */
  path: string
  /** Index of the line carrying this container's closing bracket. */
  endIndex: number
  /** Rendered in place of `tokens` while the container is folded. */
  collapsedTokens: Array<JsonToken>
}

export type JsonLine = {
  depth: number
  tokens: Array<JsonToken>
  /** Only present on lines that open a non-empty object or array. */
  fold?: JsonFold
}

const INDENT = '  '

const punctuation = (text: string): JsonToken => ({ text, kind: 'punctuation' })

// Only reached for values that survived normalization, so anything left after
// string/number/boolean is null.
const primitiveToken = (value: unknown): JsonToken =>
  typeof value === 'string'
    ? { text: JSON.stringify(value), kind: 'string' }
    : typeof value === 'number'
    ? { text: JSON.stringify(value), kind: 'number' }
    : typeof value === 'boolean'
    ? { text: String(value), kind: 'boolean' }
    : { text: 'null', kind: 'null' }

const emit = (
  value: unknown,
  depth: number,
  path: string,
  prefix: Array<JsonToken>,
  suffix: string,
  lines: Array<JsonLine>,
): void => {
  const indent = punctuation(INDENT.repeat(depth))

  if (Array.isArray(value)) {
    if (value.length === 0) {
      lines.push({
        depth,
        tokens: [indent, ...prefix, punctuation(`[]${suffix}`)],
      })
      return
    }

    const openIndex = lines.length
    lines.push({ depth, tokens: [indent, ...prefix, punctuation('[')] })

    value.forEach((item, index) => {
      emit(
        item,
        depth + 1,
        `${path}[${index}]`,
        [],
        index === value.length - 1 ? '' : ',',
        lines,
      )
    })

    lines.push({ depth, tokens: [indent, punctuation(`]${suffix}`)] })
    lines[openIndex].fold = {
      path,
      endIndex: lines.length - 1,
      collapsedTokens: [indent, ...prefix, punctuation(`[ … ]${suffix}`)],
    }
    return
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as { [key: string]: unknown })

    if (entries.length === 0) {
      lines.push({
        depth,
        tokens: [indent, ...prefix, punctuation(`{}${suffix}`)],
      })
      return
    }

    const openIndex = lines.length
    lines.push({ depth, tokens: [indent, ...prefix, punctuation('{')] })

    entries.forEach(([key, item], index) => {
      emit(
        item,
        depth + 1,
        `${path}.${key}`,
        [{ text: JSON.stringify(key), kind: 'key' }, punctuation(': ')],
        index === entries.length - 1 ? '' : ',',
        lines,
      )
    })

    lines.push({ depth, tokens: [indent, punctuation(`}${suffix}`)] })
    lines[openIndex].fold = {
      path,
      endIndex: lines.length - 1,
      collapsedTokens: [indent, ...prefix, punctuation(`{ … }${suffix}`)],
    }
    return
  }

  lines.push({
    depth,
    tokens: [
      indent,
      ...prefix,
      primitiveToken(value),
      ...(suffix ? [punctuation(suffix)] : []),
    ],
  })
}

/**
 * Normalizing through `JSON.parse(JSON.stringify(value))` applies `toJSON`
 * hooks, drops `undefined` and function members from objects, and turns them
 * into `null` inside arrays — exactly what `JSON.stringify` does. The walker
 * below therefore needs no special cases, and the round-trip invariant holds
 * by construction.
 */
export const toJsonLines = (value: unknown): Array<JsonLine> => {
  let normalized: unknown

  try {
    const serialized = JSON.stringify(value)

    if (serialized === undefined) {
      return [{ depth: 0, tokens: [{ text: 'undefined', kind: 'muted' }] }]
    }

    normalized = JSON.parse(serialized)
  } catch {
    return [
      {
        depth: 0,
        tokens: [{ text: 'Value could not be serialized', kind: 'muted' }],
      },
    ]
  }

  const lines: Array<JsonLine> = []
  emit(normalized, 0, '$', [], '', lines)
  return lines
}

export const linesToText = (lines: Array<JsonLine>): string =>
  lines
    .map((line) => line.tokens.map((token) => token.text).join(''))
    .join('\n')
